/**
 * C-323b/d · «¿Hay daño?» en la Recepción.
 *
 * El texto de «Observaciones» es sólo una nota y NO crea PNC. El PNC nace únicamente si quien recibe contesta
 * «Sí, hay daño». C-323d (decisión de Germán, 19-oct): cada daño se ELIGE, no se escribe:
 *   · Tipo de daño (catálogo) · Ubicación o pieza (catálogo) · Foto obligatoria · Descripción opcional.
 * Al completar, cada renglón se vuelve un daño del PNC con la descripción armada («Rayón · Fender izquierdo»)
 * y su foto. Los catálogos los edita admin (Calidad → PNC → «Catálogo de daños»).
 *
 *   contenedor.innerHTML = danosRecepcionHtml('nr', recepcion);   // prefijo para los ids
 *   iniciarDanosRecepcion('nr', () => marcarCambios(), { numSerie: () => serie });
 *   const { hay_dano, danos_detalle } = leerDanosRecepcion('nr');
 *   const error = validarDanosRecepcion({ hay_dano, danos_detalle }, { exigirRespuesta: true, exigirFoto: true, estadoGeneral });
 */

import api from '../services/api.js';
import recepcionService from '../services/recepcion.service.js';
import { pickImage } from './mediaChooser.js';
import { compressImage } from './imageCompressor.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ── Catálogos (se cargan una vez por sesión de página) ──
let _catalogos = null;
let _cargando = null;

export function cargarCatalogosDanos({ forzar = false } = {}) {
  if (forzar) { _catalogos = null; _cargando = null; }
  if (_catalogos) return Promise.resolve(_catalogos);
  if (!_cargando) {
    _cargando = api.get('/calidad/catalogo-danos')
      .then((r) => { _catalogos = r?.data || { tipos: [], ubicaciones: [] }; return _catalogos; })
      .catch((e) => { _cargando = null; throw e; });
  }
  return _cargando;
}

function opcionesHtml(lista, valor, placeholder) {
  const v = valor ? Number(valor) : null;
  const items = (lista || []).map((o) =>
    `<option value="${o.id}" data-exige="${o.exige_descripcion ? 1 : 0}" ${v === Number(o.id) ? 'selected' : ''}>${esc(o.nombre)}</option>`).join('');
  return `<option value="" ${v ? '' : 'selected'} disabled>${placeholder}</option>${items}`;
}

function listaDe(r) {
  let d = r?.danos_detalle;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch (e) { d = []; }
  }
  return Array.isArray(d) ? d : [];
}

function fotoHtml(url) {
  return url
    ? `<a href="${esc(url)}" target="_blank" rel="noopener"><img src="${esc(url)}" alt="Foto del daño" style="width:64px;height:48px;object-fit:cover;border-radius:4px;border:1px solid var(--gray-300);"></a>`
    : '<span style="font-size:0.75rem;color:var(--danger);">Falta la foto</span>';
}

function filaHtml(prefix, d = {}) {
  const tipos = _catalogos?.tipos || [];
  const ubic = _catalogos?.ubicaciones || [];
  return `
    <div class="dano-rec-fila" data-prefix="${prefix}" data-foto-url="${esc(d.foto_url || '')}"
      style="border:1px solid var(--gray-200);border-radius:6px;padding:0.6rem;margin-bottom:0.6rem;background:var(--bg-secondary, #fafafa);">
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:0.5rem;align-items:start;">
        <div>
          <label style="font-size:0.7rem;color:var(--gray-500);display:block;">Tipo de daño *</label>
          <select class="form-select dano-rec-tipo" data-valor="${esc(d.tipo_dano_id || '')}">${opcionesHtml(tipos, d.tipo_dano_id, '— Tipo de daño —')}</select>
        </div>
        <div>
          <label style="font-size:0.7rem;color:var(--gray-500);display:block;">¿Dónde está? *</label>
          <select class="form-select dano-rec-ubic" data-valor="${esc(d.ubicacion_id || '')}">${opcionesHtml(ubic, d.ubicacion_id, '— Ubicación o pieza —')}</select>
        </div>
        <div>
          <label style="font-size:0.7rem;color:var(--gray-500);display:block;">Foto *</label>
          <div style="display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap;">
            <span class="dano-rec-foto-vista">${fotoHtml(d.foto_url)}</span>
            <button type="button" class="btn btn-sm btn-secondary dano-rec-foto">📷 ${d.foto_url ? 'Cambiar' : 'Tomar foto'}</button>
          </div>
        </div>
      </div>
      <div style="display:flex;gap:0.5rem;align-items:center;margin-top:0.5rem;">
        <input type="text" class="form-input dano-rec-desc" maxlength="500" placeholder="Descripción (opcional)" title="Opcional; obligatoria si elegiste «Otro»"
          style="text-transform: uppercase;flex:1;" value="${esc(d.descripcion || '')}" />
        <button type="button" class="btn btn-sm btn-secondary dano-rec-quitar" title="Quitar este daño">✕</button>
      </div>
    </div>`;
}

export function danosRecepcionHtml(prefix, r = {}) {
  const hay = r.hay_dano === null || r.hay_dano === undefined ? null : Number(r.hay_dano);
  const lista = listaDe(r);
  const filas = (hay === 1 ? (lista.length ? lista : [{}]) : []).map((d) => filaHtml(prefix, d)).join('');
  return `
    <div id="${prefix}DanosBloque" style="border:1px solid var(--gray-200);border-radius:8px;padding:0.85rem;margin-bottom:1rem;background:white;">
      <div style="font-weight:700;font-size:0.9rem;margin-bottom:0.25rem;">¿Hay daño? *</div>
      <p style="font-size:0.75rem;color:var(--gray-500);margin:0 0 0.5rem;">
        Sólo si contestas «Sí» se abre un Producto No Conforme (PNC). Por cada daño elige el <strong>tipo</strong>,
        <strong>dónde está</strong> y toma una <strong>foto</strong>. Con estado general «Malo» hay que capturar al menos uno.
        Lo que escribas en «Observaciones» es una nota y no abre PNC.
      </p>
      <div style="display:flex;gap:1.25rem;flex-wrap:wrap;margin-bottom:0.5rem;">
        <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;">
          <input type="radio" name="${prefix}HayDano" value="0" ${hay === 0 ? 'checked' : ''} /> No hay daño
        </label>
        <label style="display:flex;align-items:center;gap:0.35rem;cursor:pointer;">
          <input type="radio" name="${prefix}HayDano" value="1" ${hay === 1 ? 'checked' : ''} /> Sí, hay daño
        </label>
      </div>
      <div id="${prefix}DanosDetalle" style="display:${hay === 1 ? 'block' : 'none'};">
        <div id="${prefix}DanosLista">${filas}</div>
        <button type="button" id="${prefix}AgregarDano" class="btn btn-sm btn-secondary">+ Agregar daño</button>
        <span id="${prefix}DanosAviso" style="font-size:0.75rem;color:var(--gray-500);margin-left:0.5rem;"></span>
      </div>
    </div>`;
}

/** Rellena los selects que se pintaron antes de tener el catálogo (respeta lo ya elegido en data-valor). */
function rellenarSelects(raiz) {
  if (!_catalogos || !raiz) return;
  raiz.querySelectorAll('.dano-rec-tipo').forEach((s) => {
    const v = s.value || s.dataset.valor;
    s.innerHTML = opcionesHtml(_catalogos.tipos, v, '— Tipo de daño —');
  });
  raiz.querySelectorAll('.dano-rec-ubic').forEach((s) => {
    const v = s.value || s.dataset.valor;
    s.innerHTML = opcionesHtml(_catalogos.ubicaciones, v, '— Ubicación o pieza —');
  });
}

export function iniciarDanosRecepcion(prefix, alCambiar = () => {}, opciones = {}) {
  const bloque = document.getElementById(`${prefix}DanosBloque`);
  if (!bloque) return;
  const detalle = document.getElementById(`${prefix}DanosDetalle`);
  const lista = document.getElementById(`${prefix}DanosLista`);
  const aviso = document.getElementById(`${prefix}DanosAviso`);
  const numSerie = typeof opciones.numSerie === 'function' ? opciones.numSerie : () => opciones.numSerie || '';

  cargarCatalogosDanos()
    .then(() => rellenarSelects(lista))
    .catch(() => { if (aviso) aviso.textContent = 'No se pudo cargar el catálogo de daños; recarga la página.'; });

  bloque.querySelectorAll(`input[name="${prefix}HayDano"]`).forEach((rb) => {
    rb.addEventListener('change', () => {
      const si = rb.value === '1' && rb.checked;
      if (detalle) detalle.style.display = si ? 'block' : 'none';
      if (si && lista && !lista.querySelector('.dano-rec-fila')) lista.insertAdjacentHTML('beforeend', filaHtml(prefix));
      alCambiar();
    });
  });
  document.getElementById(`${prefix}AgregarDano`)?.addEventListener('click', () => {
    lista.insertAdjacentHTML('beforeend', filaHtml(prefix));
    lista.querySelector('.dano-rec-fila:last-child .dano-rec-tipo')?.focus();
    alCambiar();
  });
  lista?.addEventListener('click', async (e) => {
    const quitar = e.target.closest('.dano-rec-quitar');
    if (quitar) {
      quitar.closest('.dano-rec-fila')?.remove();
      alCambiar();
      return;
    }
    const btnFoto = e.target.closest('.dano-rec-foto');
    if (!btnFoto) return;
    const fila = btnFoto.closest('.dano-rec-fila');
    try {
      const file = await pickImage({ title: 'Foto del daño' });
      if (!file) return;
      btnFoto.disabled = true;
      btnFoto.textContent = 'Subiendo…';
      const comprimida = await compressImage(file);
      const serie = String(numSerie() || '').trim().toUpperCase();
      const url = await recepcionService.subirFoto(
        comprimida, `${serie || 'recepcion'}_dano_${Date.now()}.jpg`, serie || undefined, 'recepciones'
      );
      fila.dataset.fotoUrl = url;
      fila.querySelector('.dano-rec-foto-vista').innerHTML = fotoHtml(url);
      btnFoto.textContent = '📷 Cambiar';
      alCambiar();
    } catch (err) {
      btnFoto.textContent = fila.dataset.fotoUrl ? '📷 Cambiar' : '📷 Tomar foto';
      if (aviso) aviso.textContent = `No se pudo subir la foto: ${err.message || err}`;
    } finally {
      btnFoto.disabled = false;
    }
  });
  lista?.addEventListener('input', () => alCambiar());
  lista?.addEventListener('change', () => alCambiar());
}

function textoOpcion(sel) {
  const o = sel && sel.selectedOptions && sel.selectedOptions[0];
  return o && o.value ? o.textContent.trim() : null;
}

export function leerDanosRecepcion(prefix) {
  const marcado = document.querySelector(`input[name="${prefix}HayDano"]:checked`);
  const hay_dano = marcado ? Number(marcado.value) : null;
  const danos_detalle = hay_dano === 1
    ? Array.from(document.querySelectorAll(`#${prefix}DanosLista .dano-rec-fila`)).map((f) => {
      const tipo = f.querySelector('.dano-rec-tipo');
      const ubic = f.querySelector('.dano-rec-ubic');
      const tipoId = Number(tipo?.value || tipo?.dataset.valor) || null;
      const ubicId = Number(ubic?.value || ubic?.dataset.valor) || null;
      return {
        tipo_dano_id: tipoId,
        ubicacion_id: ubicId,
        tipo_nombre: textoOpcion(tipo),
        ubicacion_nombre: textoOpcion(ubic),
        foto_url: f.dataset.fotoUrl || null,
        descripcion: (f.querySelector('.dano-rec-desc')?.value || '').trim().toUpperCase(),
        _exige: tipo?.selectedOptions?.[0]?.dataset.exige === '1' || ubic?.selectedOptions?.[0]?.dataset.exige === '1'
      };
    }).filter((d) => d.tipo_dano_id || d.ubicacion_id || d.foto_url || d.descripcion)
    : [];
  return { hay_dano, danos_detalle };
}

/** Quita lo que sólo sirve en pantalla antes de mandar al servidor. */
export function danosParaGuardar(danos_detalle) {
  return (danos_detalle || []).map(({ _exige, ...d }) => d);
}

/**
 * Devuelve el texto del error o null.
 *   exigirRespuesta: al completar, «¿Hay daño?» no puede quedar sin contestar.
 *   exigirFoto: al completar, cada daño necesita su foto (al guardar un borrador todavía no).
 *   estadoGeneral: con «malo» hay que capturar al menos un daño (al completar).
 */
export function validarDanosRecepcion({ hay_dano, danos_detalle }, { exigirRespuesta = false, exigirFoto = false, estadoGeneral = null } = {}) {
  if (exigirRespuesta && estadoGeneral === 'malo' && hay_dano !== 1) {
    return 'Con estado general «Malo» marca «Sí, hay daño» y captura al menos un daño (tipo, ubicación y foto)';
  }
  if (hay_dano === null || hay_dano === undefined) {
    return exigirRespuesta ? 'Contesta «¿Hay daño?» (Sí o No)' : null;
  }
  if (hay_dano === 1) {
    const lista = danos_detalle || [];
    if (!lista.length) return 'Marcaste «Sí, hay daño»: agrega al menos un daño';
    for (let i = 0; i < lista.length; i++) {
      const d = lista[i];
      const n = lista.length > 1 ? ` (daño ${i + 1})` : '';
      if (!d.tipo_dano_id) return `Elige el tipo de daño${n}`;
      if (!d.ubicacion_id) return `Elige dónde está el daño${n}`;
      if (d._exige && (d.descripcion || '').length < 5) return `Describe el daño${n}: elegiste «Otro»`;
      if (exigirFoto && !d.foto_url) return `Falta la foto del daño${n}`;
    }
  }
  return null;
}

/** Vista de sólo lectura (recepción completada). */
export function danosRecepcionLecturaHtml(r = {}) {
  const hay = r.hay_dano === null || r.hay_dano === undefined ? null : Number(r.hay_dano);
  if (hay === null) {
    return '<div class="info-item" style="margin-bottom:1rem;"><label>¿Hay daño?</label><strong style="color:var(--gray-500);">Sin contestar (recepción anterior a C-323)</strong></div>';
  }
  if (hay === 0) {
    return '<div class="info-item" style="margin-bottom:1rem;"><label>¿Hay daño?</label><strong>No hay daño</strong></div>';
  }
  const items = listaDe(r).map((d) => {
    const titulo = d.tipo_nombre || d.ubicacion_nombre
      ? `${esc(d.tipo_nombre || '¿tipo?')} · ${esc(d.ubicacion_nombre || '¿dónde?')}`
      : 'Sin tipo (captura anterior)';
    const foto = d.foto_url
      ? ` <a href="${esc(d.foto_url)}" target="_blank" rel="noopener"><img src="${esc(d.foto_url)}" alt="Foto" style="width:48px;height:36px;object-fit:cover;border-radius:4px;vertical-align:middle;"></a>`
      : '';
    return `<li style="margin-bottom:0.3rem;"><strong>${titulo}</strong>${d.descripcion ? ` — ${esc(d.descripcion)}` : ''}${foto}</li>`;
  }).join('');
  return `<div class="info-item" style="margin-bottom:1rem;"><label>¿Hay daño?</label><strong>Sí, hay daño</strong>
    <ul style="margin:0.4rem 0 0 1.1rem;padding:0;">${items}</ul>
    ${r.pnc_id ? `<a href="/calidad/pnc/${r.pnc_id}" data-link style="font-size:0.85rem;">Ver PNC-${r.pnc_id}</a>` : ''}</div>`;
}
